// credit: https://github.com/gr3enk/shadcn-ui-monthpicker/blob/main/src/components/ui/monthpicker.tsx
import dayjs from '@/lib/dayjs'
import { cn } from '@/lib/utils'
import type { VariantProps } from 'class-variance-authority'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useState, type ComponentProps, type ReactNode } from 'react'
import { buttonVariants } from './button'

type ButtonVariant = VariantProps<typeof buttonVariants>['variant']

/** A calendar month: `number` is zero-based (0 = January), matching Day.js `month()`. */
export type MonthPickerMonth = { number: number; name: string }

/** Anything the shared Day.js wrapper parses as a date, such as `'2026-01-01'`. */
export type MonthPickerDate = Date | string

export type MonthPickerCallbacks = {
  yearLabel?: (year: number) => ReactNode
  monthLabel?: (month: MonthPickerMonth) => ReactNode
}

export type MonthPickerVariant = {
  calendar?: {
    main?: ButtonVariant
    selected?: ButtonVariant
  }
  chevrons?: ButtonVariant
}

type MonthCalProps = {
  onMonthSelect?: (date: Date) => void
  selectedMonth?: MonthPickerDate
  minDate?: MonthPickerDate
  maxDate?: MonthPickerDate
  disabledDates?: MonthPickerDate[]
  callbacks?: MonthPickerCallbacks
  onYearBackward?: () => void
  onYearForward?: () => void
  variant?: MonthPickerVariant
}

export type MonthPickerProps = ComponentProps<'div'> & MonthCalProps

const MONTHS: MonthPickerMonth[][] = [
  [
    { number: 0, name: 'Jan' },
    { number: 1, name: 'Feb' },
    { number: 2, name: 'Mar' },
    { number: 3, name: 'Apr' },
  ],
  [
    { number: 4, name: 'May' },
    { number: 5, name: 'Jun' },
    { number: 6, name: 'Jul' },
    { number: 7, name: 'Aug' },
  ],
  [
    { number: 8, name: 'Sep' },
    { number: 9, name: 'Oct' },
    { number: 10, name: 'Nov' },
    { number: 11, name: 'Dec' },
  ],
]

function MonthPicker({
  onMonthSelect,
  selectedMonth,
  minDate,
  maxDate,
  disabledDates,
  callbacks,
  onYearBackward,
  onYearForward,
  variant,
  className,
  ...props
}: MonthPickerProps) {
  return (
    <div className={cn('flex w-full flex-col items-stretch gap-1', className)} {...props}>
      <MonthCal
        onMonthSelect={onMonthSelect}
        selectedMonth={selectedMonth}
        minDate={minDate}
        maxDate={maxDate}
        disabledDates={disabledDates}
        callbacks={callbacks}
        onYearBackward={onYearBackward}
        onYearForward={onYearForward}
        variant={variant}
      />
    </div>
  )
}

function MonthCal({
  selectedMonth,
  onMonthSelect,
  callbacks,
  variant,
  minDate,
  maxDate,
  disabledDates,
  onYearBackward,
  onYearForward,
}: MonthCalProps) {
  const initial = selectedMonth ? dayjs(selectedMonth) : dayjs()

  const [year, setYear] = useState(initial.year())
  const [month, setMonth] = useState(initial.month())
  const [menuYear, setMenuYear] = useState(initial.year())

  // Normalize min/max once into year/month pairs
  let minY: number | undefined
  let minM: number | undefined
  let maxY: number | undefined
  let maxM: number | undefined
  if (minDate) {
    const d = dayjs(minDate)
    minY = d.year()
    minM = d.month()
  }
  if (maxDate) {
    const d = dayjs(maxDate)
    maxY = d.year()
    maxM = d.month()
  }
  if (minDate && maxDate) {
    const minD = dayjs(minDate)
    const maxD = dayjs(maxDate)
    if (+minD > +maxD) {
      // mimic original behavior: clamp minDate to maxDate if inverted
      minY = maxD.year()
      minM = maxD.month()
      maxY = maxD.year()
      maxM = maxD.month()
    }
  }

  const disabledDatesMapped =
    disabledDates?.map((d) => {
      const dj = dayjs(d)
      return { year: dj.year(), month: dj.month() }
    }) ?? []

  // Year and month bounds are always assigned together, so checking both is the same as checking
  // the year alone; it just lets TypeScript see the month is defined too.
  const isDisabled = (y: number, m: number) => {
    const afterMax =
      maxY !== undefined && maxM !== undefined ? y > maxY || (y === maxY && m > maxM) : false

    const beforeMin =
      minY !== undefined && minM !== undefined ? y < minY || (y === minY && m < minM) : false

    const explicitlyDisabled = disabledDatesMapped.some((d) => d.year === y && d.month === m)

    return afterMax || beforeMin || explicitlyDisabled
  }

  const handleSelect = (y: number, m: number) => {
    setMonth(m)
    setYear(y)
    if (onMonthSelect) {
      const d = dayjs().year(y).month(m).date(1)
      onMonthSelect(d.toDate())
    }
  }

  return (
    <>
      {/* Header with year + chevrons */}
      <div className="relative flex items-center justify-center py-1">
        <button
          type="button"
          onClick={() => {
            setMenuYear((prev) => {
              const next = prev - 1
              if (onYearBackward) onYearBackward()
              return next
            })
          }}
          className={cn(
            buttonVariants({ variant: variant?.chevrons ?? 'outline' }),
            'absolute left-1 inline-flex h-8 w-8 items-center justify-center p-0'
          )}
          aria-label="Previous year"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div className="text-sm font-medium">
          {callbacks?.yearLabel ? callbacks.yearLabel(menuYear) : menuYear}
        </div>

        <button
          type="button"
          onClick={() => {
            setMenuYear((prev) => {
              const next = prev + 1
              if (onYearForward) onYearForward()
              return next
            })
          }}
          className={cn(
            buttonVariants({ variant: variant?.chevrons ?? 'outline' }),
            'absolute right-1 inline-flex h-8 w-8 items-center justify-center p-0'
          )}
          aria-label="Next year"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Month grid */}
      <div className="grid grid-rows-3 gap-1.5 p-1.5">
        {MONTHS.map((monthRow, rowIndex) => (
          <div key={rowIndex} className="grid grid-cols-4 gap-1.5">
            {monthRow.map((m) => {
              const disabled = isDisabled(menuYear, m.number)
              const isSelected = month === m.number && menuYear === year

              return (
                <button
                  key={m.number}
                  type="button"
                  onClick={() => !disabled && handleSelect(menuYear, m.number)}
                  disabled={disabled}
                  className={cn(
                    buttonVariants({
                      variant: isSelected
                        ? (variant?.calendar?.selected ?? 'default')
                        : (variant?.calendar?.main ?? 'ghost'),
                    }),
                    'h-9 w-full min-w-0 px-2 text-sm font-normal aria-selected:opacity-100'
                  )}
                >
                  {callbacks?.monthLabel ? callbacks.monthLabel(m) : m.name}
                </button>
              )
            })}
          </div>
        ))}
      </div>
    </>
  )
}

MonthPicker.displayName = 'MonthPicker'

export { MonthPicker }
